import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Money,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mlm/ui';
import { labelizeLedgerEntry, labelizeSourcePlan } from '@mlm/shared';
import {
  adminGetUserReferralCodes,
  adminListSlots,
  adminReleaseReferralCode,
  adminResetReferralChanges,
  api,
  apiErrorMessage,
  getMemberSubscription,
  getTenantFeatures,
  getUserBranches,
  grantMemberSubscription,
  listArchiveRequests,
  revokeMemberSubscription,
  setUserBranches,
  unarchiveUser,
} from '../lib/api';
import { listCreators } from '../lib/creators-api';
import { useBranches, useConfinedBranchIds } from '../lib/branch-scope';
import { useHasPerm } from '../lib/auth-store';
import { AdjustDialog } from '../components/adjust-dialog';
import { SlotCodePicker } from '../components/slot-code-picker';

type UserDetail = {
  id: string;
  email: string;
  fullName: string | null;
  status: string;
  /** Set when the inactivity sweep blocked the account (null for a manual block). */
  inactivityBlockedAt: string | null;
  kycStatus: string;
  registrationData: Record<string, string | { r2Key: string; filename: string }> | null;
  totpEnabled: boolean;
  // QoL Slice 5b — true when the user has a POS manager PIN stored. Drives
  // the Set vs Replace/Clear actions on the Access tab.
  posManagerPinSet: boolean;
  /** Admin authoritative sponsor override slot_code; null when unset. */
  sponsorOverrideSlotCode: string | null;
  /**
   * The slot this member's signup link resolved to (`registered_under_slot_id`).
   * Read-only history of what happened at signup — NOT the override above, and
   * not necessarily the genealogy sponsor their slots ended up under.
   */
  linkSponsor: { slotCode: string; ownerFullName: string | null } | null;
  roles: string[];
  perms: string[];
  branchId: string | null;
  /** Admin visibility set; empty = unconfined. */
  branchIds: string[];
  createdAt: string;
};

type Wallet = {
  userId?: string;
  available: string;
  pending: string;
  lifetimeEarned: string;
  availableGc?: string;
  lifetimeGcEarned?: string;
  slotCount?: number;
  updatedAt: string | null;
};

type SlotWallet = {
  slotId: string;
  slotCode: string;
  available: string;
  pending: string;
  lifetimeEarned: string;
  availableGc: string;
  lifetimeGcEarned: string;
  updatedAt: string | null;
};

type LedgerRow = {
  id: number | string;
  slotId: string | null;
  kind: string;
  level: number | null;
  amount: string;
  currency: string;
  direction: 'C' | 'D';
  sourceEventId: string;
  sourcePlan: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

export function MemberDetailPage() {
  const { id } = useParams<{ id: string }>();

  const userQ = useQuery({
    queryKey: ['user', id],
    queryFn: async () => (await api.get<UserDetail>(`/users/${id}`)).data,
    enabled: !!id,
  });

  const walletQ = useQuery({
    queryKey: ['user-wallet', id],
    queryFn: async () => (await api.get<Wallet>(`/wallet/${id}`)).data,
    enabled: !!id,
    retry: false,
  });

  const slotWalletsQ = useQuery({
    queryKey: ['user-slot-wallets', id],
    queryFn: async () => (await api.get<SlotWallet[]>(`/wallet/${id}/slots`)).data,
    enabled: !!id,
  });

  const ledgerQ = useQuery({
    queryKey: ['user-ledger', id],
    queryFn: async () =>
      (
        await api.get<LedgerRow[]>(`/wallet/${id}/ledger`, {
          params: { page: 1, pageSize: 50 },
        })
      ).data,
    enabled: !!id,
  });

  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const kindLabels = featuresQ.data?.ledger_labels;
  const planLabels = featuresQ.data?.ledger_source_plan_labels;

  // Filter by owner id, NOT by email. `ownerSearch` is a fuzzy substring
  // search whose `_`/`%` are LIKE wildcards, so an email like
  // `a_b@x.com` also matched `a.b@x.com` and listed that other member's
  // slots on this page (mhyel, 2026-09-09).
  const slotsQ = useQuery({
    queryKey: ['user-slots', id],
    queryFn: () => adminListSlots({ userId: id, pageSize: 50 }),
    enabled: !!id,
  });

  // Per-tab access: every tab is independently lockable. Slots/Wallet/Verification
  // load from their own endpoint perm; Information + Beneficiary gate the
  // user.read payload (presentation locks, frontend-enforced).
  const canViewInfo = useHasPerm('member.detail.info.read');
  const canViewSlots = useHasPerm('slot.read');
  const canViewWallet = useHasPerm('member.wallet.read');
  const canViewKyc = useHasPerm('kyc.read');
  const canViewBeneficiary = useHasPerm('member.detail.beneficiary.read');
  // Subscription reads the same payload Information does (user.read) and
  // writes under user.write, so the tab shows wherever member detail does and
  // the buttons appear only for an admin who may change the member.
  const canViewSubscription =
    canViewInfo &&
    (featuresQ.data?.lms_enabled === true || featuresQ.data?.member_subscription_enabled === true);
  // First allowed tab so locking Information never strands the page.
  const firstTab =
    [
      canViewInfo && 'information',
      canViewSlots && 'slots',
      canViewWallet && 'wallet',
      canViewKyc && 'verification',
      canViewBeneficiary && 'beneficiary',
      canViewSubscription && 'subscription',
    ].find(Boolean) || 'information';

  if (userQ.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (userQ.error || !userQ.data) {
    return <p className="text-sm text-destructive">Member not found.</p>;
  }

  const u = userQ.data;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/members" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back to members
        </Link>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">{u.fullName ?? u.email}</h1>
          <p className="text-sm text-muted-foreground mt-1">{u.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant={
              u.status === 'active' ? 'default' : u.status === 'archived' ? 'muted' : 'secondary'
            }
          >
            {u.status}
          </Badge>
          <Badge variant={u.kycStatus === 'verified' ? 'default' : 'outline'}>
            KYC: {u.kycStatus}
          </Badge>
          {u.totpEnabled && <Badge variant="secondary">2FA</Badge>}
        </div>
      </div>

      <Tabs defaultValue={firstTab}>
        <TabsList className="flex flex-wrap h-auto">
          {canViewInfo && <TabsTrigger value="information">Information</TabsTrigger>}
          {canViewSlots && <TabsTrigger value="slots">Slots</TabsTrigger>}
          {canViewWallet && <TabsTrigger value="wallet">Wallet & Activity</TabsTrigger>}
          {canViewKyc && <TabsTrigger value="verification">Verification</TabsTrigger>}
          {canViewBeneficiary && <TabsTrigger value="beneficiary">Beneficiary</TabsTrigger>}
          {canViewSubscription && <TabsTrigger value="subscription">Subscription</TabsTrigger>}
        </TabsList>

        {canViewInfo && (
          <TabsContent value="information" className="space-y-4">
            <InformationTab user={u} />
            <RegistrationDetailsCard user={u} />
          </TabsContent>
        )}
        {canViewSlots && (
          <TabsContent value="slots" className="space-y-4">
            <SlotsTab
              userId={id!}
              slots={slotsQ.data?.rows ?? []}
              loading={slotsQ.isLoading}
              slotWallets={slotWalletsQ.data ?? []}
            />
            <ReferralCodesCard userId={id!} />
          </TabsContent>
        )}
        {canViewWallet && (
          <TabsContent value="wallet">
            <WalletTab
              wallet={walletQ.data ?? null}
              slotWallets={slotWalletsQ.data ?? []}
              ledger={ledgerQ.data ?? []}
              ledgerLoading={ledgerQ.isLoading}
              kindLabels={kindLabels}
              planLabels={planLabels}
            />
          </TabsContent>
        )}
        {canViewKyc && (
          <TabsContent value="verification">
            <VerificationTab user={u} />
          </TabsContent>
        )}
        {canViewSubscription && (
          <TabsContent value="subscription">
            <SubscriptionTab userId={id!} />
          </TabsContent>
        )}
        {canViewBeneficiary && (
          <TabsContent value="beneficiary">
            <Card>
              <CardContent className="p-8 text-center text-sm text-muted-foreground">
                Beneficiary records require a dedicated schema (TBD). Ships in a later phase.
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/**
 * When this member was archived. `users` carries no `archived_at` column, so
 * the only record of the moment is the request row that caused it — look it up
 * and simply omit the date when we can't (a staff account without
 * `view.members.archive`, or a request old enough to have paged off). A
 * missing date must never stop the notice itself from rendering: "you cannot
 * edit this account" is the part the operator needs.
 */
/**
 * The completed archive request for this member, when the operator may read it.
 * Its `executedMode` is what separates a member who can be restored from one
 * whose identity was overwritten and cannot.
 */
function useArchiveRecord(userId: string, archived: boolean) {
  const canRead = useHasPerm('view.members.archive');
  const { data } = useQuery({
    queryKey: ['admin-archive-requests', 'completed', 1],
    queryFn: () => listArchiveRequests({ status: 'completed', page: 1, pageSize: 100 }),
    enabled: archived && canRead,
  });
  return data?.items.find((r) => r.userId === userId) ?? null;
}

function InformationTab({ user }: { user: UserDetail }) {
  const queryClient = useQueryClient();
  // An archived user's PATCH is refused server-side with `user_archived`, so
  // the form is disabled rather than left clickable — an admin should not have
  // to discover a permanent state by failing a save.
  const archived = user.status === 'archived';
  const archiveRecord = useArchiveRecord(user.id, archived);
  const archivedAt = archiveRecord?.decidedAt ?? null;
  // No record at all means an admin set the status by hand — nothing was
  // scrubbed, so it is restorable. Only a recorded erase is permanent.
  const erased = archiveRecord?.executedMode === 'erased';
  const canArchive = useHasPerm('member.archive');
  const unarchive = useMutation({
    mutationFn: () => unarchiveUser(user.id),
    meta: { successMessage: 'Account restored.' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user', user.id] }),
  });
  const [status, setStatus] = useState(user.status);
  const [kyc, setKyc] = useState(user.kycStatus);
  const [fullName, setFullName] = useState(user.fullName ?? '');
  const [phone, setPhone] = useState('');
  const [overrideCode, setOverrideCode] = useState<string | null>(
    user.sponsorOverrideSlotCode ?? null,
  );

  const patchUser = useMutation({
    mutationFn: async (patch: Record<string, unknown>) =>
      (await api.patch(`/users/${user.id}`, patch)).data,
    meta: { successMessage: 'Member updated.' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user', user.id] }),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm max-w-2xl">
          {archived && (
            <div className="space-y-2 rounded-md border bg-muted/40 p-3">
              <div className="font-medium">
                {erased ? 'Erased' : 'Closed'}
                {archivedAt ? ` on ${new Date(archivedAt).toLocaleDateString()}` : ''}
              </div>
              <p className="text-muted-foreground">
                {erased
                  ? 'This member asked to be erased. Their email, phone, password, KYC and profile were scrubbed and cannot be edited or restored. Their slots, purchases, certificates and place in the genealogy tree are preserved.'
                  : 'This member asked to close their account. Nothing was deleted: their profile, slots, purchases, certificates and place in the genealogy tree are all intact. They can return by signing in and choosing Reactivate, or you can restore the account now.'}
              </p>
              {archiveRecord?.reactivatedAt && (
                <p className="text-muted-foreground">
                  Previously reopened on{' '}
                  {new Date(archiveRecord.reactivatedAt).toLocaleDateString()}.
                </p>
              )}
              {!erased && canArchive && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={unarchive.isPending}
                  onClick={() => unarchive.mutate()}
                >
                  {unarchive.isPending ? 'Restoring…' : 'Restore account'}
                </Button>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Full name</label>
              <Input
                value={fullName}
                disabled={archived}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Phone</label>
              <Input
                value={phone}
                disabled={archived}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(leave blank to keep)"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Status</label>
              <Select value={status} onValueChange={setStatus} disabled={archived}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">active</SelectItem>
                  <SelectItem value="pending">pending</SelectItem>
                  <SelectItem value="suspended">suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">KYC</label>
              <Select value={kyc} onValueChange={setKyc} disabled={archived}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">none</SelectItem>
                  <SelectItem value="submitted">submitted</SelectItem>
                  <SelectItem value="verified">verified</SelectItem>
                  <SelectItem value="rejected">rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Email: <span className="font-mono">{user.email}</span> (immutable) · Joined{' '}
            {new Date(user.createdAt).toLocaleDateString()}
          </div>
          <Button
            disabled={patchUser.isPending || archived}
            onClick={() =>
              patchUser.mutate({
                status,
                kycStatus: kyc,
                fullName: fullName || undefined,
                phone: phone || undefined,
              })
            }
          >
            {patchUser.isPending ? 'Saving…' : 'Save changes'}
          </Button>

          {/* Which replicated link this member signed up through. Read-only: it
            records what happened at signup. It drives the FIRST slot's sponsor
            by default, but a typed sponsorCode at redeem or the override below
            can still win — so it may differ from the slot's actual sponsor. */}
          <div className="mt-2 space-y-1 rounded-md border p-3">
            <div className="text-xs font-medium">Replicated link sponsor</div>
            {user.linkSponsor ? (
              <p className="text-sm">
                <span className="font-mono">{user.linkSponsor.slotCode}</span>
                {user.linkSponsor.ownerFullName && (
                  <span className="text-muted-foreground"> — {user.linkSponsor.ownerFullName}</span>
                )}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                —{' '}
                <span className="text-xs">
                  (walk-in signup, or registered before links were tracked)
                </span>
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              The slot this member&apos;s signup link pointed at. Their first slot defaults to it; a
              sponsor typed at activation or the override below still wins.
            </p>
          </div>

          {/* Authoritative per-user sponsor override. Forces every new slot this
            member creates under the chosen sponsor slot, winning even over a
            sponsor they type at redeem. Empty = normal sponsor resolution. */}
          <div className="mt-2 space-y-2 rounded-md border p-3">
            <div className="text-xs">
              <div className="font-medium">Sponsor override</div>
              <p className="text-muted-foreground">
                Forces <strong>every new slot</strong> this member creates under the chosen sponsor
                slot — even over a sponsor they type at activation. Leave empty for normal sponsor
                resolution.
              </p>
            </div>
            <SlotCodePicker value={overrideCode} onChange={setOverrideCode} disabled={archived} />
            <Button
              variant="outline"
              size="sm"
              disabled={patchUser.isPending || archived}
              onClick={() => patchUser.mutate({ sponsorOverrideSlotCode: overrideCode ?? '' })}
            >
              {patchUser.isPending ? 'Saving…' : 'Save sponsor override'}
            </Button>
          </div>

          {/* Explicit account-level block — distinct from the Messages-tab chat
            block (which only mutes chat). Blocking suspends the account so the
            member can no longer log in; it does not delete any data. */}
          <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
            <div className="text-xs">
              <div className="font-medium">
                {user.status === 'suspended'
                  ? user.inactivityBlockedAt
                    ? 'Account blocked for inactivity'
                    : 'Account blocked'
                  : 'Account access'}
              </div>
              <p className="text-muted-foreground">
                {archived
                  ? 'An archived account already holds an unusable credential — there is nothing left to block or unblock.'
                  : user.status === 'suspended' && user.inactivityBlockedAt
                    ? `Blocked automatically on ${new Date(user.inactivityBlockedAt).toLocaleDateString()} for going too long with no new direct and no repeat purchase. Unblocking restores access and restarts their inactivity clock.`
                    : user.status === 'suspended'
                      ? 'This member is blocked and cannot log in. Unblock to restore full access.'
                      : 'Block to suspend the account — the member can no longer log in. No data is deleted.'}
              </p>
            </div>
            <Button
              variant={user.status === 'suspended' ? 'outline' : 'destructive'}
              disabled={patchUser.isPending || archived}
              onClick={() => {
                const block = user.status !== 'suspended';
                const ok = window.confirm(
                  block
                    ? `Block ${user.fullName || user.email}? They will be unable to log in until unblocked.`
                    : `Unblock ${user.fullName || user.email}? They will be able to log in again.`,
                );
                if (!ok) return;
                const next = block ? 'suspended' : 'active';
                setStatus(next);
                patchUser.mutate({ status: next });
              }}
            >
              {user.status === 'suspended' ? 'Unblock account' : 'Block account'}
            </Button>
          </div>
        </CardContent>
      </Card>
      <BranchAccessCard user={user} />
    </div>
  );
}

/**
 * Which branches this admin user may SEE — Dashboard, Orders, Reports and
 * Products all narrow to them. Empty = unconfined (the whole tenant).
 *
 * Deliberately NOT on the Access page: that manages ROLES, and branch
 * visibility is a per-user property (the legacy `scope_branch_id` is per-role,
 * which is exactly the confusion this avoids). Gated on `user.branch.assign`,
 * which is seeded non-grantable — a tenant admin cannot delegate it.
 */
function BranchAccessCard({ user }: { user: UserDetail }) {
  const queryClient = useQueryClient();
  const canAssign = useHasPerm('user.branch.assign');
  const confinedSelf = useConfinedBranchIds().length > 0;
  const { data: branches } = useBranches();
  const branchesQ = useQuery({
    queryKey: ['user-branches', user.id],
    queryFn: () => getUserBranches(user.id),
  });
  const [draft, setDraft] = useState<string[] | null>(null);
  const selected = draft ?? branchesQ.data ?? [];

  const save = useMutation({
    mutationFn: () => setUserBranches(user.id, selected),
    meta: { successMessage: 'Branch access updated.' },
    onSuccess: () => {
      setDraft(null);
      queryClient.invalidateQueries({ queryKey: ['user-branches', user.id] });
      queryClient.invalidateQueries({ queryKey: ['user', user.id] });
    },
  });

  const isSuperAdminTarget = user.roles.includes('super_admin');
  // A confined actor must not edit the containment boundary — the server 403s
  // this too; the UI just doesn't pretend otherwise.
  const editable = canAssign && !confinedSelf && !isSuperAdminTarget;

  const toggle = (id: string) =>
    setDraft(selected.includes(id) ? selected.filter((b) => b !== id) : [...selected, id]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Branch access</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm max-w-2xl">
        <p className="text-xs text-muted-foreground">
          Tick the branches this user may see. With none ticked they see the whole tenant.
          Dashboard, Commerce&nbsp;&rarr;&nbsp;Orders, Reports and Products all narrow to the ticked
          branches. This does not change their POS branch.
        </p>
        {isSuperAdminTarget ? (
          <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
            Super admins are never branch-restricted.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              {(branches ?? []).map((b) => (
                <label key={b.id} className="flex items-center gap-2">
                  <Checkbox
                    checked={selected.includes(b.id)}
                    disabled={!editable || save.isPending}
                    onCheckedChange={() => toggle(b.id)}
                  />
                  <span>
                    {b.name} <span className="text-muted-foreground">({b.code})</span>
                  </span>
                </label>
              ))}
            </div>
            {editable && draft !== null && (
              <div className="flex gap-2">
                <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
                  {save.isPending ? 'Saving…' : 'Save branch access'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
              </div>
            )}
            {editable && (
              <p className="text-[11px] text-muted-foreground">
                Takes effect on their next sign-in or token refresh (within ~15 minutes).
              </p>
            )}
            {!editable && !isSuperAdminTarget && (
              <p className="text-[11px] text-muted-foreground">
                {confinedSelf
                  ? 'Your own account is branch-restricted, so you cannot change branch access.'
                  : 'You do not have permission to change branch access.'}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Custom registration fields captured at signup. Labels come from the tenant
 * config (`registration_fields`); values from the member's `registration_data`.
 * File values get a signed-URL download link.
 */
function RegistrationDetailsCard({ user }: { user: UserDetail }) {
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const fields = [...(featuresQ.data?.registration_fields ?? [])].sort((a, b) => a.order - b.order);
  if (fields.length === 0) return null;

  const openFile = async (fieldId: string) => {
    const { data } = await api.get<{ url: string; filename: string }>(
      `/users/${user.id}/registration-file/${fieldId}`,
    );
    window.open(data.url, '_blank', 'noopener');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Registration details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm max-w-2xl">
        {fields.map((f) => {
          const v = user.registrationData?.[f.id];
          return (
            <div
              key={f.id}
              className="flex items-center justify-between gap-3 border-b pb-2 last:border-0"
            >
              <span className="text-muted-foreground">{f.label}</span>
              {v == null ? (
                <span className="text-muted-foreground/60">—</span>
              ) : f.type === 'file' && typeof v !== 'string' ? (
                <Button size="sm" variant="outline" onClick={() => openFile(f.id)}>
                  {v.filename || 'Download'}
                </Button>
              ) : (
                <span className="font-medium">{typeof v === 'string' ? v : v.filename}</span>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function SlotsTab({
  userId,
  slots,
  loading,
  slotWallets,
}: {
  userId: string;
  slots: Array<{
    id: string;
    slotCode: string;
    status: string;
    membershipName: string;
    activatedAt: string | null;
    // The GENEALOGY sponsor (`slots.sponsor_slot_id`), already on the row the
    // list endpoint returns. Not the replicated-link sponsor, which is a
    // different question and lives on the Slots page as its own column.
    sponsorSlotCode: string | null;
    sponsorFullName: string | null;
    sponsorEmail: string | null;
  }>;
  loading: boolean;
  slotWallets: SlotWallet[];
}) {
  const walletBySlot = new Map(slotWallets.map((w) => [w.slotId, w]));
  const canAdjust = useHasPerm('member.balance.adjust');
  const canSeeIncome = useHasPerm('member.income.view');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Slots owned</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <p className="p-6 text-sm text-muted-foreground">Loading…</p>
        ) : !slots.length ? (
          <p className="p-6 text-sm text-muted-foreground">This member has no slots yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="px-4 py-2 font-medium">Slot</th>
                <th className="px-4 py-2 font-medium">Tier</th>
                <th className="px-4 py-2 font-medium">Sponsor</th>
                <th className="px-4 py-2 font-medium">Status</th>
                {canSeeIncome && <th className="px-4 py-2 font-medium text-right">Available</th>}
                {canSeeIncome && <th className="px-4 py-2 font-medium text-right">Lifetime</th>}
                {canAdjust && <th className="px-4 py-2 font-medium text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {slots.map((s) => {
                const w = walletBySlot.get(s.id);
                return (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2 font-mono text-xs">
                      <Link to={`/slots/${s.id}`} className="hover:underline">
                        {s.slotCode}
                      </Link>
                    </td>
                    <td className="px-4 py-2">{s.membershipName}</td>
                    <td className="px-4 py-2">
                      {s.sponsorSlotCode ? (
                        <span>
                          {s.sponsorFullName ?? s.sponsorEmail ?? 'Unknown'}{' '}
                          <span className="font-mono text-xs text-muted-foreground">
                            ({s.sponsorSlotCode})
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={s.status === 'active' ? 'default' : 'outline'}>
                        {s.status}
                      </Badge>
                    </td>
                    {canSeeIncome && (
                      <td className="px-4 py-2 text-right">
                        {w?.available != null ? (
                          <Money amount={w.available} direction="neutral" />
                        ) : null}
                      </td>
                    )}
                    {canSeeIncome && (
                      <td className="px-4 py-2 text-right">
                        {w?.lifetimeEarned != null ? (
                          <Money amount={w.lifetimeEarned} direction="neutral" />
                        ) : null}
                      </td>
                    )}
                    {canAdjust && (
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <AdjustDialog
                            kind="wallet"
                            userId={userId}
                            slotId={s.id}
                            slotCode={s.slotCode}
                          />
                          <AdjustDialog
                            kind="points"
                            userId={userId}
                            slotId={s.id}
                            slotCode={s.slotCode}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function WalletTab({
  wallet,
  slotWallets,
  ledger,
  ledgerLoading,
  kindLabels,
  planLabels,
}: {
  wallet: Wallet | null;
  slotWallets: SlotWallet[];
  ledger: LedgerRow[];
  ledgerLoading: boolean;
  kindLabels?: Record<string, string>;
  planLabels?: Record<string, string>;
}) {
  return (
    <div className="space-y-4">
      {wallet && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Wallet totals (across slots)</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-4 text-sm">
            <Stat label="Available" value={wallet.available} />
            <Stat label="Pending" value={wallet.pending} />
            <Stat label="Lifetime" value={wallet.lifetimeEarned} />
          </CardContent>
        </Card>
      )}

      {slotWallets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Per-slot wallets</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Slot</th>
                  <th className="px-4 py-2 font-medium text-right">Available</th>
                  <th className="px-4 py-2 font-medium text-right">Pending</th>
                  <th className="px-4 py-2 font-medium text-right">Lifetime</th>
                  <th className="px-4 py-2 font-medium text-right">GC avail</th>
                </tr>
              </thead>
              <tbody>
                {slotWallets.map((w) => (
                  <tr key={w.slotId} className="border-b last:border-0">
                    <td className="px-4 py-2 font-mono text-xs">
                      <Link to={`/slots/${w.slotId}`} className="hover:underline">
                        {w.slotCode}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-right">{w.available}</td>
                    <td className="px-4 py-2 text-right">{w.pending}</td>
                    <td className="px-4 py-2 text-right">{w.lifetimeEarned}</td>
                    <td className="px-4 py-2 text-right">{w.availableGc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ledger (latest 50)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {ledgerLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !ledger.length ? (
            <p className="p-6 text-sm text-muted-foreground">No ledger entries.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Plan</th>
                  <th className="px-4 py-2 font-medium">Kind</th>
                  <th className="px-4 py-2 font-medium">Lvl</th>
                  <th className="px-4 py-2 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((r) => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-xs" title={r.sourcePlan}>
                      {labelizeSourcePlan(r.sourcePlan, planLabels)}
                    </td>
                    <td className="px-4 py-2" title={r.kind}>
                      {labelizeLedgerEntry(r.kind, r.metadata, kindLabels)}
                    </td>
                    <td className="px-4 py-2 text-xs">{r.level ?? ''}</td>
                    <td className="px-4 py-2 text-right">
                      <Money
                        amount={r.amount}
                        currency={r.currency}
                        direction={r.direction === 'C' ? 'credit' : 'debit'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReferralCodesCard({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['user-referral-codes', userId],
    queryFn: () => adminGetUserReferralCodes(userId),
  });
  const release = useMutation({
    mutationFn: (code: string) => adminReleaseReferralCode(code),
    meta: { successMessage: 'Referral code released.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-referral-codes', userId] }),
  });
  const resetChanges = useMutation({
    mutationFn: () => adminResetReferralChanges(userId),
    meta: { successMessage: 'Change count reset.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-referral-codes', userId] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Referral codes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm max-w-2xl">
        {q.isLoading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : !q.data?.codes.length ? (
          <p className="text-muted-foreground">No referral codes.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-1.5">Code</th>
                <th className="py-1.5">Status</th>
                <th className="py-1.5 text-right">Used</th>
                <th className="py-1.5" />
              </tr>
            </thead>
            <tbody>
              {q.data.codes.map((c) => (
                <tr key={c.code} className="border-b last:border-0">
                  <td className="py-1.5 font-mono">{c.code}</td>
                  <td className="py-1.5">
                    <Badge variant={c.status === 'active' ? 'default' : 'secondary'}>
                      {c.status}
                    </Badge>
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{c.useCount}</td>
                  <td className="py-1.5 text-right">
                    {c.status === 'reserved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={release.isPending}
                        onClick={() => release.mutate(c.code)}
                      >
                        Release
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex items-center justify-between gap-3 border-t pt-3">
          <p className="text-xs text-muted-foreground">
            Code changes used: <b>{q.data?.referralCodeChanges ?? 0}</b>. Reset to let the member
            change their code again.
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={resetChanges.isPending}
            onClick={() => resetChanges.mutate()}
          >
            {resetChanges.isPending ? 'Resetting…' : 'Reset change count'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

type KycSubmissionDetail = {
  id: string;
  status: 'submitted' | 'verified' | 'rejected';
  documents: Array<{ kind: string; r2Key: string }>;
  rejectionReason: string | null;
  createdAt: string;
} | null;

function VerificationTab({ user }: { user: UserDetail }) {
  const qc = useQueryClient();
  const canReview = useHasPerm('kyc.review');
  const [rejectReason, setRejectReason] = useState('');

  const submissionQ = useQuery({
    queryKey: ['user-kyc', user.id],
    queryFn: async () => (await api.get<KycSubmissionDetail>(`/kyc/by-user/${user.id}`)).data,
  });
  const submission = submissionQ.data;

  const docsQ = useQuery({
    queryKey: ['user-kyc-docs', submission?.id],
    enabled: !!submission?.id,
    queryFn: async () =>
      (
        await api.get<{ documents: Array<{ kind: string; url: string }> }>(
          `/kyc/${submission!.id}/documents`,
        )
      ).data.documents,
  });

  const review = useMutation({
    mutationFn: async (vars: { decision: 'verified' | 'rejected'; rejectionReason?: string }) =>
      (await api.post(`/kyc/${submission!.id}/review`, vars)).data,
    meta: { successMessage: 'KYC reviewed.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', user.id] });
      qc.invalidateQueries({ queryKey: ['user-kyc', user.id] });
      setRejectReason('');
    },
  });

  const pending = submission?.status === 'submitted';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">KYC verification</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div>
          Current status: <Badge>{user.kycStatus}</Badge>
        </div>

        {submission?.rejectionReason && (
          <p className="text-xs text-destructive">
            Last rejection reason: {submission.rejectionReason}
          </p>
        )}

        {!submission ? (
          <p className="text-xs text-muted-foreground">
            This member has not submitted any KYC documents yet.
          </p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              {docsQ.isLoading && (
                <p className="text-xs text-muted-foreground">Loading documents…</p>
              )}
              {docsQ.data?.map((d) => (
                <div key={d.kind} className="space-y-1">
                  <div className="text-xs text-muted-foreground">{d.kind.replace(/_/g, ' ')}</div>
                  <a href={d.url} target="_blank" rel="noreferrer">
                    <img
                      src={d.url}
                      alt={d.kind}
                      className="aspect-[4/3] w-full rounded-md border object-cover"
                    />
                  </a>
                </div>
              ))}
            </div>

            {canReview && pending && (
              <div className="space-y-2 border-t pt-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    disabled={review.isPending}
                    onClick={() => review.mutate({ decision: 'verified' })}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={review.isPending || rejectReason.trim() === ''}
                    onClick={() =>
                      review.mutate({ decision: 'rejected', rejectionReason: rejectReason.trim() })
                    }
                  >
                    Reject
                  </Button>
                  <Input
                    className="h-8 max-w-xs"
                    placeholder="Rejection reason (required to reject)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </div>
              </div>
            )}
            {!pending && (
              <p className="text-xs text-muted-foreground">
                Submission {submission.status}. The member can re-submit to reopen review.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * ADMIN-GRANTED SUBSCRIPTION — "tick the member as subscribed, then decide
 * until when".
 *
 * The tenant's `lms_subscription_model` decides which store a grant writes,
 * and the SERVER decides it, not this form. Both stores are shown regardless:
 * a tenant that switched models leaves live windows behind in the other one,
 * and hiding them is how an admin grants access somebody already has.
 *
 * Two ways to set the end, because admins ask for both: an absolute date
 * ("paid up to the 31st") and a stack-from-current extension ("+30 days"). The
 * extension never shortens an existing window — that rule lives on the server
 * so a grant made through the API behaves the same way.
 */
function SubscriptionTab({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const canWrite = useHasPerm('user.write');
  const [mode, setMode] = useState<'days' | 'until'>('days');
  const [days, setDays] = useState('30');
  const [until, setUntil] = useState('');
  const [creatorUserId, setCreatorUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const subQ = useQuery({
    queryKey: ['member-subscription', userId],
    queryFn: () => getMemberSubscription(userId),
  });
  const model = subQ.data?.model ?? 'tenant';
  // Only needed for the creator model; skipped entirely under `tenant` so the
  // tab costs one request on the common path.
  const creatorsQ = useQuery({
    queryKey: ['creators', 'approved-picker'],
    queryFn: () => listCreators({ status: 'approved', pageSize: 200 }),
    enabled: model === 'creator',
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['member-subscription', userId] });
    setError(null);
  };

  const grant = useMutation({
    mutationFn: () =>
      grantMemberSubscription(userId, {
        ...(model === 'creator' ? { creatorUserId } : {}),
        ...(mode === 'days'
          ? { addDays: Number(days) }
          : // End of the chosen day in the admin's own timezone: a bare date
            // would expire the grant at local midnight, a day early from the
            // point of view of whoever typed it.
            { endsAt: new Date(`${until}T23:59:59`).toISOString() }),
      }),
    onSuccess: invalidate,
    onError: (e) => setError(apiErrorMessage(e)),
  });

  const revoke = useMutation({
    mutationFn: (creator?: string) =>
      revokeMemberSubscription(userId, creator ? { creatorUserId: creator } : {}),
    onSuccess: invalidate,
    onError: (e) => setError(apiErrorMessage(e)),
  });

  const formValid =
    (mode === 'days' ? Number(days) > 0 : !!until) && (model === 'tenant' || !!creatorUserId);
  const tenant = subQ.data?.tenant ?? null;
  const creatorRows = subQ.data?.creator ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Subscription
            <Badge variant="outline">
              {model === 'creator' ? 'Per-creator model' : 'Tenant-wide model'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {subQ.isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : (
            <>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Tenant-wide subscription
                  {model === 'creator' && ' (not what gates courses right now)'}
                </div>
                {tenant ? (
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Badge variant={tenant.isActive ? 'default' : 'muted'}>
                      {tenant.isActive ? 'Active' : 'Lapsed'}
                    </Badge>
                    <span className="text-muted-foreground">
                      {tenant.currentPeriodEnd
                        ? `Until ${new Date(tenant.currentPeriodEnd).toLocaleDateString()}`
                        : 'No period set'}
                    </span>
                    <span className="text-muted-foreground">
                      {tenant.provider === 'manual'
                        ? 'Granted by admin'
                        : `Pays by ${tenant.provider}`}
                    </span>
                    {canWrite && tenant.isActive && model === 'tenant' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate(undefined)}
                      >
                        End now
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="mt-2 text-muted-foreground">Never subscribed.</p>
                )}
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Per-creator subscriptions
                  {model === 'tenant' && ' (not what gates courses right now)'}
                </div>
                {creatorRows.length === 0 ? (
                  <p className="mt-2 text-muted-foreground">None.</p>
                ) : (
                  <table className="mt-2 w-full text-sm">
                    <thead className="text-xs text-muted-foreground">
                      <tr>
                        <th className="p-2 text-left">Creator</th>
                        <th className="p-2 text-left">Expires</th>
                        <th className="p-2 text-left">State</th>
                        <th className="p-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {creatorRows.map((r) => (
                        <tr key={r.creatorUserId} className="border-t">
                          <td className="p-2">{r.creatorName}</td>
                          <td className="p-2">{new Date(r.expiresAt).toLocaleDateString()}</td>
                          <td className="p-2">
                            <Badge variant={r.active ? 'default' : 'muted'}>
                              {r.active ? 'Active' : 'Lapsed'}
                            </Badge>
                          </td>
                          <td className="p-2 text-right">
                            {canWrite && r.active && model === 'creator' && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={revoke.isPending}
                                onClick={() => revoke.mutate(r.creatorUserId)}
                              >
                                End now
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {canWrite && (
        <Card>
          <CardHeader>
            <CardTitle>Grant or extend</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Opens access without charging anything. Adding days extends from the current expiry,
              so a member who already has time left keeps it.
            </p>
            {model === 'creator' && (
              <div className="grid gap-1 max-w-sm">
                <label className="text-xs text-muted-foreground">Creator</label>
                <Select value={creatorUserId} onValueChange={setCreatorUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a creator" />
                  </SelectTrigger>
                  <SelectContent>
                    {(creatorsQ.data?.rows ?? []).map((c) => (
                      <SelectItem key={c.userId} value={c.userId}>
                        {c.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex flex-wrap items-end gap-3">
              <div className="grid gap-1">
                <label className="text-xs text-muted-foreground">How long</label>
                <Select value={mode} onValueChange={(v) => setMode(v as 'days' | 'until')}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">Add days</SelectItem>
                    <SelectItem value="until">Until a date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {mode === 'days' ? (
                <>
                  <div className="grid gap-1">
                    <label className="text-xs text-muted-foreground">Days</label>
                    <Input
                      className="w-28"
                      inputMode="numeric"
                      value={days}
                      onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, ''))}
                    />
                  </div>
                  <div className="flex gap-2">
                    {['30', '90', '365'].map((d) => (
                      <Button key={d} size="sm" variant="outline" onClick={() => setDays(d)}>
                        +{d}
                      </Button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="grid gap-1">
                  <label className="text-xs text-muted-foreground">Until</label>
                  <Input
                    type="date"
                    className="w-44"
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                  />
                </div>
              )}
              <Button disabled={!formValid || grant.isPending} onClick={() => grant.mutate()}>
                {grant.isPending ? 'Granting…' : 'Grant'}
              </Button>
            </div>
            {error && <p className="text-destructive">{error}</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-mono">{value}</div>
    </div>
  );
}
